import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Stack,
  Container
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Bolt,
  Paid,
  Receipt,
  TrendingUp,
  Sync,
  AccountBalance,
  CheckCircle,
  CurrencyExchange,
  Schedule
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import paymentImage from "../../../Components/globalAssets/blackwhite.png";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const BlitzPayFeatures = [
  {
    icon: <Bolt fontSize="large" />,
    title: "Instant Payments",
    description: "Convert Net 30/60 terms to immediate cash with our instant payment options"
  },
  {
    icon: <Receipt fontSize="large" />,
    title: "Automated Invoicing",
    description: "Generate professional invoices with automated reminders and tracking"
  },
  {
    icon: <TrendingUp fontSize="large" />,
    title: "Cash Flow Optimization",
    description: "Predict and manage your cash flow with AI-powered forecasting tools"
  },
  {
    icon: <Sync fontSize="large" />,
    title: "Payment Automation",
    description: "Schedule recurring payments and automate approvals workflows"
  }
];

export default function SolutionsModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = theme.palette.primary.main;
  const statsColor = theme.palette.secondary.main;

  const refs = useRef({});
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);

  const FeatureCard = ({ icon, title, description }) => (
    <Paper
      component={motion.div}
      variants={fadeIn}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: theme.shadows[2],
        transition: "transform 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6]
        },
        bgcolor: theme.palette.background.paper
      }}
    >
      <Box sx={{ color: accentColor, mb: 2 }}>{icon}</Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
        {description}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: theme.palette.background.default }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: 10,
          bgcolor: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant={isMobile ? "h4" : "h2"}
                gutterBottom
                sx={{ fontWeight: 800, color: theme.palette.primary.contrastText }}
              >
                Revolutionize Your Creator Payments
              </Typography>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant="h5"
                sx={{ mb: 4, fontWeight: 300, color: theme.palette.primary.contrastText }}
              >
                Get paid instantly, manage cash flow, and automate payables with BlitzPay
              </Typography>
              <Button
                component={motion.div}
                variants={fadeIn}
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleNavigate}
                sx={{
                  fontSize: "1.1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: theme.shadows[4]
                }}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                variants={fadeIn}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 4,
                  p: 2,
                  boxShadow: theme.shadows[10]
                }}
              >
                <img 
                  src={paymentImage} 
                  alt="BlitzPay Dashboard" 
                  style={{ 
                    width: '100%', 
                    borderRadius: 12,
                    border: `1px solid ${theme.palette.divider}` 
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: theme.palette.dark }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              { value: "90%", label: "Faster Payments" },
              { value: "24h", label: "Average Payout Time" },
              { value: "3.8x", label: "Faster Invoice Clearance" },
              { value: "97%", label: "Payment Success Rate" }
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box textAlign="center">
                  <Typography
                    variant="h2"
                    component={motion.div}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    sx={{ 
                      fontWeight: 800, 
                      color: statsColor,
                      mb: 1
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        id="features" 
        ref={el => (refs.current['features'] = el)} 
        sx={{ py: 8, bgcolor: theme.palette.background.paper }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ fontWeight: 700, mb: 6, color: theme.palette.text.primary }}
          >
            Powerful Payment Solutions
          </Typography>
          
          <Grid container spacing={4}>
            {BlitzPayFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard 
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box 
        id="how-it-works" 
        ref={el => (refs.current['how-it-works'] = el)} 
        sx={{ py: 8, bgcolor: theme.palette.dark }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ fontWeight: 700, mb: 6, color: theme.palette.text.primary }}
          >
            Streamlined Payment Workflow
          </Typography>
          
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <List sx={{ width: '100%' }}>
                {[
                  {
                    Icon: <Receipt sx={{ color: accentColor }} />,
                    title: "Submit Invoice",
                    text: "Creators upload invoices directly through our platform"
                  },
                  {
                    Icon: <Schedule sx={{ color: accentColor }} />,
                    title: "Set Payment Terms",
                    text: "Choose Net 30/60 or request instant payment"
                  },
                  {
                    Icon: <CurrencyExchange sx={{ color: accentColor }} />,
                    title: "Automatic Conversion",
                    text: "We handle currency conversion at competitive rates"
                  },
                  {
                    Icon: <Paid sx={{ color: accentColor }} />,
                    title: "Get Paid",
                    text: "Funds deposited directly to your account in 24 hours"
                  }
                ].map((item, index) => (
                  <ListItem 
                    key={index} 
                    sx={{ alignItems: 'flex-start', mb: 2, p: 0 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                      {item.Icon}
                    </ListItemIcon>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[4]
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mb: 3,
                  p: 2,
                  bgcolor: theme.palette.dark,
                  borderRadius: 2
                }}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                    Standard Payment
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                    Net 30 Terms
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                    BlitzPay Instant
                  </Typography>
                </Box>
                
                {[
                  { feature: "Payment Time", standard: "2-5 days", net30: "30+ days", blitz: "<24h" },
                  { feature: "Cash Flow Impact", standard: "Moderate", net30: "Negative", blitz: "Positive" },
                  { feature: "Fee", standard: "2-3%", net30: "0%", blitz: "1.5-3%" },
                  { feature: "FX Fees", standard: "3-5%", net30: "3-5%", blitz: "0.5-1.5%" }
                ].map((row, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      p: 2,
                      bgcolor: index % 2 === 0 ? theme.palette.action.hover : 'transparent',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                      {row.feature}
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                      {row.standard}
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                      {row.net30}
                    </Typography>
                    <Typography variant="body1" sx={{ color: accentColor, fontWeight: 600 }}>
                      {row.blitz}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Cash Flow Management */}
      <Box id="cashflow" ref={el => (refs.current['cashflow'] = el)} sx={{ py: 8, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 4,
                  p: 2,
                  boxShadow: theme.shadows[2],
                  height: "100%"
                }}
              >
                <img 
                  src={paymentImage} 
                  alt="Cash Flow Dashboard" 
                  style={{ 
                    width: '100%', 
                    borderRadius: 12,
                    border: `1px solid ${theme.palette.divider}` 
                  }} 
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Intelligent Cash Flow Management
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 3, color: theme.palette.text.secondary }}>
                Gain complete control over your finances with our advanced cash flow tools:
              </Typography>
              
              <List>
                {[
                  "Predict future cash flow with AI-powered forecasting",
                  "Visualize payment timelines and receivables",
                  "Optimize payment schedules for maximum liquidity",
                  "Identify cash flow gaps before they become problems",
                  "Generate detailed financial reports in one click"
                ].map((item, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36, color: accentColor }}>
                      <CheckCircle />
                    </ListItemIcon>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>{item}</Typography>
                  </ListItem>
                ))}
              </List>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleNavigate}
                sx={{
                  mt: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 2,
                  "&:hover": { borderWidth: 2 },
                  color: accentColor,
                  borderColor: accentColor
                }}
              >
                Explore Cash Flow Tools
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonial */}
      <Box sx={{ py: 8, bgcolor: theme.palette.dark }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              bgcolor: theme.palette.background.paper,
              boxShadow: theme.shadows[2]
            }}
          >
            <AccountBalance sx={{ fontSize: 60, color: accentColor, mb: 2 }} />
            <Typography variant="h5" paragraph sx={{ fontStyle: "italic", fontWeight: 300, color: theme.palette.text.primary }}>
              "BlitzPay transformed how we manage creator payments. What used to take 45 days now happens in 24 hours. Our creators are happier, and our finance team saves 20 hours a week."
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              — Head of Finance, Global Media Agency
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 8, bgcolor: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" textAlign="center" spacing={3}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.contrastText }}>
              Ready to Accelerate Your Payments?
            </Typography>
            <Typography variant="h5" sx={{ color: theme.palette.primary.contrastText, fontWeight: 300 }}>
              Join thousands of creators and brands using BlitzPay
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleNavigate}
              sx={{
                fontSize: "1.1rem",
                px: 6,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: theme.shadows[10],
                mt: 2
              }}
            >
              Get Started Free
            </Button>
          </Stack>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}